import { useEffect, useRef, useState } from 'react'

import type { NavigationSource } from '../navigationListContext'
import { useNavigationListContext } from '../navigationListContext'

/**
 * This hooks allows you to know if an item from a NavigationList is selected.
 * You can optionally listen to changes to react to selection state. The source
 * passed to the callback will indicate from where the selection change was
 * triggered.
 */
export function useIsSelectedNavigationListItem(
  id: string,
  onChange?: (source: NavigationSource) => void
) {
  const { selectedId$ } = useNavigationListContext()

  const [isSelected, setIsSelected] = useState(selectedId$.value?.id === id)

  // We use refs here to prevent un-necessary subscription mount/unmount
  const previousIsSelected = useRef(isSelected)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const subscription = selectedId$.subscribe((selection) => {
      const newIsSelected = selection?.id === id
      if (newIsSelected === previousIsSelected.current) {
        return
      }
      previousIsSelected.current = newIsSelected
      setIsSelected(newIsSelected)
      if (newIsSelected) {
        onChangeRef.current?.(selection.source)
      }
    })
    return () => subscription.unsubscribe()
  }, [selectedId$, id])

  return isSelected
}
